// src/pages/DashboardControlRiders.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Table,
} from "react-bootstrap";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from "recharts";

import { getVentas } from "../helpers/rutaVentas";
import {
  getPagosDashboardControl,
} from "../helpers/rutaDashboardControl";

/*
  Reutilizamos el diseño del Dashboard Financiero.
  NO modificamos ese dashboard.
*/
import "../css/dashboard-financiero.css";


/* =========================================================
   HELPERS GENERALES
   ========================================================= */

const clean = (v) =>
  String(v ?? "").trim();


const norm = (v) =>
  clean(v)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toUpperCase();


const round2 = (n) =>
  Math.round((Number(n) || 0) * 100) / 100;


const todayLocalISO = () => {
  const d = new Date();

  d.setMinutes(
    d.getMinutes() -
      d.getTimezoneOffset()
  );

  return d
    .toISOString()
    .slice(0, 10);
};


const firstDayMonthISO = () => {
  const d = new Date();

  d.setDate(1);

  d.setMinutes(
    d.getMinutes() -
      d.getTimezoneOffset()
  );

  return d
    .toISOString()
    .slice(0, 10);
};


const formatUSD = (value) => {
  const n = Number(value || 0);

  return n.toLocaleString(
    "es-AR",
    {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }
  );
};


const formatDate = (value) => {
  if (!value) return "-";

  if (
    typeof value === "string"
  ) {
    const m =
      value.match(
        /^(\d{4})-(\d{2})-(\d{2})/
      );

    if (m) {
      return `${m[3]}/${m[2]}/${m[1]}`;
    }
  }

  const d = new Date(value);

  if (
    Number.isNaN(d.getTime())
  ) {
    return "-";
  }

  return d.toLocaleDateString(
    "es-AR"
  );
};


const getId = (value) => {
  if (!value) return "";

  if (
    typeof value === "string"
  ) {
    return value;
  }

  return (
    value._id ||
    value.id ||
    ""
  );
};


/* =========================================================
   USUARIOS / VENDEDORES
   ========================================================= */

const nombreUsuario = (
  usuario
) => {
  if (!usuario) return "";

  if (
    typeof usuario === "string"
  ) {
    return "";
  }

  const nombre1 = [
    usuario.nombres,
    usuario.apellidos,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  const nombre2 = [
    usuario.nombre,
    usuario.apellido,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    nombre1 ||
    nombre2 ||
    usuario.email ||
    ""
  );
};


const nombreVendedorVenta = (
  venta = {}
) => {
  return (
    clean(
      venta.vendedorNombre
    ) ||
    clean(
      venta.usuarioNombre
    ) ||
    nombreUsuario(
      venta.vendedor
    ) ||
    nombreUsuario(
      venta.usuario
    ) ||
    "SIN VENDEDOR"
  );
};


/* =========================================================
   SEDE
   ========================================================= */
const getSedeVenta = (
  venta = {}
) => {
  const sede = norm(
    venta?.vendedor?.sede ||
      venta?.usuario?.sede ||
      ""
  )
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (sede === "MIAMI") {
    return "MIAMI";
  }

  if (sede === "YERBA BUENA") {
    return "YERBA BUENA";
  }

  if (sede === "TUCUMAN") {
    return "TUCUMAN";
  }

  return "RESTO";
};


/* =========================================================
   RECEPCIÓN
   ========================================================= */

const getEstadoRecepcion = (
  venta = {}
) => {
  return norm(
    venta.estadoRecepcion
  ) === "PENDIENTE"
    ? "PENDIENTE"
    : "COMPLETA";
};


/* =========================================================
   FINANCIERA
   ========================================================= */

const nombreFinanciera = (
  financiera
) => {
  if (!financiera) {
    return "-";
  }

  if (
    typeof financiera ===
    "string"
  ) {
    return financiera;
  }

  return (
    financiera.financiera ||
    financiera.razonsocial ||
    "-"
  );
};


const esCreditoFinanciera = (
  pago = {}
) =>
  norm(
    pago.medioPago
  ) ===
  "CREDITO FINANCIERA";


/* =========================================================
   PRODUCTO
   ========================================================= */

const nombreProducto = (
  item = {}
) => {
  return (
    clean(
      item.descripcionProducto
    ) ||
    clean(
      item.modeloProducto
    ) ||
    clean(
      item.producto
        ?.nombreComercial
    ) ||
    clean(
      item.producto?.modelo
    ) ||
    "SIN PRODUCTO"
  );
};



/* =========================================================
   COMPONENTE
   ========================================================= */

export default function DashboardControlRiders() {

  const [desde, setDesde] =
    useState(
      firstDayMonthISO()
    );

  const [hasta, setHasta] =
    useState(
      todayLocalISO()
    );

  const [sedeFiltro, setSedeFiltro] =
    useState("");

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const [ventas, setVentas] =
    useState([]);


  /* =======================================================
     CARGA GENERAL
     ======================================================= */

  const cargarDashboard =
    useCallback(
      async () => {

        setLoading(true);
        setError("");

        try {

          /* -----------------------------------------------
             1. TRAER TODAS LAS VENTAS DEL PERÍODO
             ----------------------------------------------- */

          const LIMIT = 200;

          let page = 1;
          let total = 0;

          let ventasBase = [];

          do {

            const resp =
              await getVentas({
                page,
                limit: LIMIT,
                q: "",
                estado: "",
                desde,
                hasta,
              });

            const lote =
              Array.isArray(
                resp?.ventas
              )
                ? resp.ventas
                : [];

            ventasBase =
              ventasBase.concat(
                lote
              );

            total =
              Number(
                resp?.total ||
                  lote.length ||
                  0
              );

            page += 1;

          } while (
            ventasBase.length <
            total
          );


        /* -----------------------------------------------
        2. TRAER TODOS LOS PAGOS EN UNA SOLA CONSULTA
        ----------------------------------------------- */

        const ventaIds =
        ventasBase
            .map((venta) =>
            getId(venta)
            )
            .filter(Boolean);


        /*
        Una única llamada al backend.
        Esta ruta SOLO LEE.
        */
        const respPagos =
        await getPagosDashboardControl(
            ventaIds
        );


        const pagosMasivos =
        Array.isArray(
            respPagos?.pagos
        )
            ? respPagos.pagos
            : [];


        /*
        Agrupamos los pagos por venta
        en memoria.

        Esto es prácticamente instantáneo
        y evita consultar nuevamente MongoDB.
        */
        const pagosPorVenta =
        new Map();


        pagosMasivos.forEach(
        (pago) => {
            const ventaId =
            getId(
                pago.venta
            );

            if (!ventaId) {
            return;
            }

            if (
            !pagosPorVenta.has(
                ventaId
            )
            ) {
            pagosPorVenta.set(
                ventaId,
                []
            );
            }

            pagosPorVenta
            .get(
                ventaId
            )
            .push(
                pago
            );
        }
        );


        /*
        La Venta ya tiene almacenados:
        - totalUSD
        - totalAcreditadoUSD
        - saldoPendienteUSD
        - estadoPago

        Esos campos se actualizan en las rutas
        operativas cuando cambia un pago.

        El dashboard simplemente los LEE.
        */
        const ventasCompletas =
        ventasBase.map(
            (venta) => {

            const ventaId =
                getId(
                venta
                );


            const pagos =
                pagosPorVenta.get(
                ventaId
                ) || [];


            const totalVenta =
                Number(
                venta.totalUSD ||
                    0
                );


            const totalAcreditadoUSD =
                Number(
                venta.totalAcreditadoUSD ||
                    0
                );


            const saldoGuardado =
                venta.saldoPendienteUSD;


            const saldoPendienteUSD =
                saldoGuardado !==
                undefined &&
                saldoGuardado !==
                null
                ? Number(
                    saldoGuardado ||
                        0
                    )
                : Math.max(
                    totalVenta -
                        totalAcreditadoUSD,
                    0
                    );


            return {
                ...venta,

                _pagos:
                pagos,

                _totalVenta:
                totalVenta,

                _totalAcreditado:
                totalAcreditadoUSD,

                _saldo:
                saldoPendienteUSD,

                _sede:
                getSedeVenta(
                    venta
                ),

                _vendedor:
                nombreVendedorVenta(
                    venta
                ),

                _recepcion:
                getEstadoRecepcion(
                    venta
                ),
            };
            }
        );


          setVentas(
            ventasCompletas
          );

        } catch (err) {

          console.error(
            "Error cargando Centro de Control:",
            err
          );

          setError(
            err?.response
              ?.data?.error ||
              err?.response
                ?.data?.message ||
              err?.message ||
              "No se pudo cargar el Centro de Control."
          );

          setVentas([]);

        } finally {

          setLoading(false);
        }
      },
      [
        desde,
        hasta,
      ]
    );


  useEffect(() => {

    cargarDashboard();

  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  /* =======================================================
     FILTRO POR SEDE
     ======================================================= */

  const ventasFiltradas =
    useMemo(() => {

      const activas =
        ventas.filter(
          (venta) =>
            norm(
              venta.estado
            ) !== "ANULADA"
        );

      if (!sedeFiltro) {
        return activas;
      }

      return activas.filter(
        (venta) =>
          venta._sede ===
          sedeFiltro
      );

    }, [
      ventas,
      sedeFiltro,
    ]);


  /* =======================================================
     KPIs GENERALES
     ======================================================= */

  const kpis =
    useMemo(() => {

      const cantidad =
        ventasFiltradas.length;


      const totalVentas =
        ventasFiltradas.reduce(
          (acc, venta) =>
            acc +
            Number(
              venta._totalVenta ||
                0
            ),
          0
        );


      const acreditado =
        ventasFiltradas.reduce(
          (acc, venta) =>
            acc +
            Number(
              venta._totalAcreditado ||
                0
            ),
          0
        );


      const saldo =
        ventasFiltradas.reduce(
          (acc, venta) =>
            acc +
            Number(
              venta._saldo ||
                0
            ),
          0
        );


      const ventasConSaldo =
        ventasFiltradas.filter(
          (venta) =>
            Number(
              venta._saldo ||
                0
            ) > 0.01
        );


      const recepcionesPendientes =
        ventasFiltradas.filter(
          (venta) =>
            venta._recepcion ===
            "PENDIENTE"
        );


      let pagosPendientes = 0;

      let contratosPendientes = 0;

      let operacionesDiferencia =
        0;

      let diferenciaAbsoluta =
        0;


      ventasFiltradas.forEach(
        (venta) => {

          const pagos =
            Array.isArray(
              venta._pagos
            )
              ? venta._pagos
              : [];


          pagos.forEach(
            (pago) => {

              if (
                pago?.activo ===
                  false ||
                norm(
                  pago
                    .estadoAcreditacion
                ) === "ANULADO"
              ) {
                return;
              }


              if (
                norm(
                  pago
                    .estadoAcreditacion
                ) === "PENDIENTE"
              ) {
                pagosPendientes += 1;
              }


              if (
                esCreditoFinanciera(
                  pago
                ) &&
                !pago
                  .contratoFirmado
              ) {
                contratosPendientes +=
                  1;
              }


              if (
                esCreditoFinanciera(
                  pago
                )
              ) {

                const diferencia =
                  Number(
                    pago
                      .diferenciaFinancieraUSD ||
                      0
                  );

                if (
                  Math.abs(
                    diferencia
                  ) > 1
                ) {

                  operacionesDiferencia +=
                    1;

                  diferenciaAbsoluta +=
                    Math.abs(
                      diferencia
                    );
                }
              }
            }
          );
        }
      );


      const ticketPromedio =
        cantidad > 0
          ? totalVentas /
            cantidad
          : 0;


      const porcentajeCobrado =
        totalVentas > 0
          ? (acreditado /
              totalVentas) *
            100
          : 0;


      return {

        cantidadVentas:
          cantidad,

        totalVentas:
          round2(
            totalVentas
          ),

        acreditado:
          round2(
            acreditado
          ),

        saldo:
          round2(
            saldo
          ),

        ticketPromedio:
          round2(
            ticketPromedio
          ),

        porcentajeCobrado:
          round2(
            porcentajeCobrado
          ),

        ventasConSaldo:
          ventasConSaldo.length,

        recepcionesPendientes:
          recepcionesPendientes.length,

        pagosPendientes,

        contratosPendientes,

        operacionesDiferencia,

        diferenciaAbsoluta:
          round2(
            diferenciaAbsoluta
          ),
      };

    }, [
      ventasFiltradas,
    ]);


  /* =======================================================
     VENTAS POR SEDE
     ======================================================= */

  const ventasPorSede =
    useMemo(() => {

      const mapa =
        new Map();

        ventasFiltradas.forEach(

          (venta) => {

            const sede =
              venta._sede ||
              "RESTO";


            if (
              !mapa.has(
                sede
              )
            ) {
              mapa.set(
                sede,
                {
                  sede,
                  cantidad: 0,
                  total: 0,
                }
              );
            }


            const item =
              mapa.get(
                sede
              );


            item.cantidad +=
              1;

            item.total +=
              Number(
                venta._totalVenta ||
                  0
              );
          }
        );


      return Array.from(
        mapa.values()
      )
        .map(
          (item) => ({
            ...item,
            total:
              round2(
                item.total
              ),
          })
        )
        .sort(
          (a, b) =>
            b.total -
            a.total
        );

    }, [
    ventasFiltradas,
    ]);


  /* =======================================================
     RANKING VENDEDORES
     ======================================================= */

  const rankingVendedores =
    useMemo(() => {

      const mapa =
        new Map();


      ventasFiltradas.forEach(
        (venta) => {

          const vendedor =
            venta._vendedor ||
            "SIN VENDEDOR";


          if (
            !mapa.has(
              vendedor
            )
          ) {

            mapa.set(
              vendedor,
              {
                vendedor,
                ventas: 0,
                total: 0,
                acreditado: 0,
              }
            );
          }


          const item =
            mapa.get(
              vendedor
            );


          item.ventas +=
            1;

          item.total +=
            Number(
              venta._totalVenta ||
                0
            );

          item.acreditado +=
            Number(
              venta
                ._totalAcreditado ||
                0
            );
        }
      );


      return Array.from(
        mapa.values()
      )
        .map(
          (item) => ({
            ...item,

            total:
              round2(
                item.total
              ),

            acreditado:
              round2(
                item.acreditado
              ),
          })
        )
        .sort(
          (a, b) =>
            b.total -
            a.total
        )
        .slice(
          0,
          8
        );

    }, [
      ventasFiltradas,
    ]);


  /* =======================================================
     PRODUCTOS MÁS VENDIDOS
     ======================================================= */

  const topProductos =
    useMemo(() => {

      const mapa =
        new Map();


      ventasFiltradas.forEach(
        (venta) => {

          const items =
            Array.isArray(
              venta.items
            )
              ? venta.items
              : [];


          items.forEach(
            (item) => {

              const producto =
                nombreProducto(
                  item
                );


              if (
                !mapa.has(
                  producto
                )
              ) {

                mapa.set(
                  producto,
                  {
                    producto,
                    cantidad: 0,
                    total: 0,
                  }
                );
              }


              const fila =
                mapa.get(
                  producto
                );


              const cantidad =
                Number(
                  item.cantidad ||
                    1
                );


              fila.cantidad +=
                cantidad;


              fila.total +=
                Number(
                  item.subtotalUSD ||
                    0
                );
            }
          );
        }
      );


      return Array.from(
        mapa.values()
      )
        .map(
          (item) => ({
            ...item,
            total:
              round2(
                item.total
              ),
          })
        )
        .sort(
          (a, b) =>
            b.cantidad -
            a.cantidad
        )
        .slice(
          0,
          8
        );

    }, [
      ventasFiltradas,
    ]);


  /* =======================================================
     EVOLUCIÓN DE VENTAS
     ======================================================= */

  const evolucionVentas =
    useMemo(() => {

      const mapa =
        new Map();


      ventasFiltradas.forEach(
        (venta) => {

          const fecha =
            formatDate(
              venta.fecha
            );


          if (
            !mapa.has(
              fecha
            )
          ) {

            mapa.set(
              fecha,
              {
                fecha,
                ventas: 0,
                cantidad: 0,
              }
            );
          }


          const item =
            mapa.get(
              fecha
            );


          item.ventas +=
            Number(
              venta._totalVenta ||
                0
            );

          item.cantidad +=
            1;
        }
      );


      return Array.from(
        mapa.values()
      )
        .map(
          (item) => ({
            ...item,
            ventas:
              round2(
                item.ventas
              ),
          })
        )
        .sort(
          (a, b) => {

            const [
              da,
              ma,
              ya,
            ] =
              a.fecha.split(
                "/"
              );

            const [
              db,
              mb,
              yb,
            ] =
              b.fecha.split(
                "/"
              );


            return (
              new Date(
                `${ya}-${ma}-${da}`
              ) -
              new Date(
                `${yb}-${mb}-${db}`
              )
            );
          }
        );

    }, [
      ventasFiltradas,
    ]);


  /* =======================================================
     DIFERENCIAS FINANCIERAS
     ======================================================= */

  const diferenciasFinancieras =
    useMemo(() => {

      const diferencias =
        [];


      ventasFiltradas.forEach(
        (venta) => {

          const pagos =
            Array.isArray(
              venta._pagos
            )
              ? venta._pagos
              : [];


          pagos.forEach(
            (pago) => {

              if (
                !esCreditoFinanciera(
                  pago
                )
              ) {
                return;
              }


              if (
                pago?.activo ===
                  false ||
                norm(
                  pago
                    .estadoAcreditacion
                ) === "ANULADO"
              ) {
                return;
              }


              const diferencia =
                Number(
                  pago
                    .diferenciaFinancieraUSD ||
                    0
                );


              if (
                Math.abs(
                  diferencia
                ) <= 1
              ) {
                return;
              }


              diferencias.push({
                venta:
                  venta.nroventa,

                fecha:
                  venta.fecha,

                financiera:
                  nombreFinanciera(
                    pago.financiera
                  ),

                esperado:
                  Number(
                    pago
                      .fondeoEsperadoUSD ||
                      0
                  ),

                real:
                  Number(
                    pago
                      .fondeoFinancieraUSD ||
                      pago
                        .totalCobrarClienteUSD ||
                      0
                  ),

                diferencia,

                ajuste:
                  Number(
                    pago
                      .ajusteFinancieroUSD ||
                      0
                  ),
              });
            }
          );
        }
      );


      return diferencias
        .sort(
          (a, b) =>
            Math.abs(
              b.diferencia
            ) -
            Math.abs(
              a.diferencia
            )
        )
        .slice(
          0,
          10
        );

    }, [
      ventasFiltradas,
    ]);


  /* =======================================================
     VENTAS CON SALDO
     ======================================================= */

  const principalesSaldos =
    useMemo(() => {

      return [
        ...ventasFiltradas,
      ]
        .filter(
          (venta) =>
            Number(
              venta._saldo ||
                0
            ) > 0.01
        )
        .sort(
          (a, b) =>
            Number(
              b._saldo ||
                0
            ) -
            Number(
              a._saldo ||
                0
            )
        )
        .slice(
          0,
          8
        );

    }, [
      ventasFiltradas,
    ]);


  /* =======================================================
     LIMPIAR
     ======================================================= */

  const limpiarFiltros =
    () => {

      setDesde(
        firstDayMonthISO()
      );

      setHasta(
        todayLocalISO()
      );

      setSedeFiltro("");
    };


  /* =======================================================
     RENDER
     ======================================================= */

  return (

    <div
      className="
        dashboard-financiero
        dashboard-light
        container-fluid
      "
    >

      {/* ===================================================
          CABECERA
         =================================================== */}

      <div
        className="
          d-flex
          flex-wrap
          justify-content-between
          align-items-center
          gap-2
          mb-3
        "
      >

        <div>

          <h3 className="mb-1">
            Centro de Control Riders 360
          </h3>

          <small className="text-muted">
            Estado general de ventas, cobranzas, recepción,
            financieras y operación comercial.
          </small>

        </div>


        <div className="d-flex gap-2">

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={
              limpiarFiltros
            }
          >
            Limpiar
          </Button>


          <Button
            variant="dark"
            size="sm"
            onClick={
              cargarDashboard
            }
            disabled={
              loading
            }
          >
            {loading
              ? "Buscando..."
              : "Actualizar"}
          </Button>

        </div>

      </div>


      {/* ===================================================
          FILTROS
         =================================================== */}

      <Row className="g-2 mb-3">

        <Col
          xs={12}
          md={3}
        >

          <Form.Label>
            Desde
          </Form.Label>

          <Form.Control
            type="date"
            size="sm"
            value={desde}
            onChange={(e) =>
              setDesde(
                e.target.value
              )
            }
          />

        </Col>


        <Col
          xs={12}
          md={3}
        >

          <Form.Label>
            Hasta
          </Form.Label>

          <Form.Control
            type="date"
            size="sm"
            value={hasta}
            onChange={(e) =>
              setHasta(
                e.target.value
              )
            }
          />

        </Col>


        <Col
          xs={12}
          md={6}
        >

          <Form.Label>
            Sede
          </Form.Label>

          <div
            className="
              d-flex
              flex-wrap
              gap-1
            "
          >

            {[
              ["", "TODAS"],
              [
                "MIAMI",
                "MIAMI",
              ],
              [
                "YERBA BUENA",
                "YERBA BUENA",
              ],
              [
                "TUCUMAN",
                "TUCUMAN",
              ],
            ].map(
              ([
                value,
                label,
              ]) => (

                <Button
                  key={
                    value ||
                    "TODAS"
                  }
                  size="sm"
                  variant={
                    sedeFiltro ===
                    value
                      ? "primary"
                      : "outline-primary"
                  }
                  onClick={() =>
                    setSedeFiltro(
                      value
                    )
                  }
                >
                  {label}
                </Button>

              )
            )}

          </div>

        </Col>

      </Row>


      {error && (

        <div className="alert alert-danger py-2">
          {error}
        </div>

      )}


      {loading ? (

        <div className="text-center py-5">

          <Spinner animation="border" />

          <div className="mt-2">
            Analizando operación...
          </div>

        </div>

      ) : (

        <>

          {/* ===============================================
              KPIs
             =============================================== */}

          <Row
            xs={1}
            sm={2}
            lg={4}
            className="
              g-3
              mb-3
              dashboard-kpis
            "
          >

            <Col>

              <Card className="dashboard-kpi">

                <Card.Body>

                  <div className="kpi-icon kpi-blue">
                    USD
                  </div>

                  <div>

                    <small>
                      Ventas del período
                    </small>

                    <h4>
                      {formatUSD(
                        kpis.totalVentas
                      )}
                    </h4>

                    <div className="kpi-sub">
                      {kpis.cantidadVentas} ventas
                    </div>

                  </div>

                </Card.Body>

              </Card>

            </Col>


            <Col>

              <Card className="dashboard-kpi">

                <Card.Body>

                  <div className="kpi-icon kpi-green">
                    ✓
                  </div>

                  <div>

                    <small>
                      Total acreditado
                    </small>

                    <h4 className="text-neto">
                      {formatUSD(
                        kpis.acreditado
                      )}
                    </h4>

                    <div className="kpi-sub">
                      {kpis.porcentajeCobrado.toFixed(
                        1
                      )}
                      % de ventas
                    </div>

                  </div>

                </Card.Body>

              </Card>

            </Col>


            <Col>

              <Card className="dashboard-kpi">

                <Card.Body>

                  <div className="kpi-icon kpi-red">
                    !
                  </div>

                  <div>

                    <small>
                      Saldo pendiente
                    </small>

                    <h4 className="text-fee">
                      {formatUSD(
                        kpis.saldo
                      )}
                    </h4>

                    <div className="kpi-sub">
                      {kpis.ventasConSaldo} ventas
                    </div>

                  </div>

                </Card.Body>

              </Card>

            </Col>


            <Col>

              <Card className="dashboard-kpi">

                <Card.Body>

                  <div className="kpi-icon kpi-purple">
                    Ø
                  </div>

                  <div>

                    <small>
                      Ticket promedio
                    </small>

                    <h4>
                      {formatUSD(
                        kpis.ticketPromedio
                      )}
                    </h4>

                    <div className="kpi-sub">
                      Promedio por venta
                    </div>

                  </div>

                </Card.Body>

              </Card>

            </Col>

          </Row>


          {/* ===============================================
              REQUIERE ATENCIÓN
             =============================================== */}

          <Card
            className="
              dashboard-panel
              mb-3
            "
            style={{
              borderLeft:
                "5px solid #ef4444",
            }}
          >

            <Card.Body>

              <div
                className="
                  d-flex
                  justify-content-between
                  align-items-center
                  mb-3
                "
              >

                <div>

                  <h5 className="mb-1">
                    Requiere atención
                  </h5>

                  <small className="text-muted">
                    Situaciones que necesitan revisión operativa.
                  </small>

                </div>

                <span
                  className="
                    badge
                    bg-danger
                  "
                >
                  CONTROL OPERATIVO
                </span>

              </div>


              <Row className="g-2">

                <Col
                  xs={12}
                  md={6}
                  xl={3}
                >

                  <div
                    className="
                      border
                      rounded
                      p-3
                      h-100
                    "
                  >

                    <div
                      className="
                        text-danger
                        fw-bold
                      "
                    >
                      Ventas con saldo
                    </div>

                    <h4 className="my-1">
                      {kpis.ventasConSaldo}
                    </h4>

                    <small className="text-muted">
                      {formatUSD(
                        kpis.saldo
                      )}{" "}
                      pendientes
                    </small>

                  </div>

                </Col>


                <Col
                  xs={12}
                  md={6}
                  xl={3}
                >

                  <div
                    className="
                      border
                      rounded
                      p-3
                      h-100
                    "
                  >

                    <div
                      style={{
                        color:
                          "#d97706",
                        fontWeight:
                          800,
                      }}
                    >
                      Recepciones pendientes
                    </div>

                    <h4 className="my-1">
                      {
                        kpis.recepcionesPendientes
                      }
                    </h4>

                    <small className="text-muted">
                      Ventas pendientes de receptor
                    </small>

                  </div>

                </Col>


                <Col
                  xs={12}
                  md={6}
                  xl={3}
                >

                  <div
                    className="
                      border
                      rounded
                      p-3
                      h-100
                    "
                  >

                    <div
                      style={{
                        color:
                          "#7c3aed",
                        fontWeight:
                          800,
                      }}
                    >
                      Diferencias financieras
                    </div>

                    <h4 className="my-1">
                      {
                        kpis.operacionesDiferencia
                      }
                    </h4>

                    <small className="text-muted">
                      {formatUSD(
                        kpis.diferenciaAbsoluta
                      )}{" "}
                      en diferencias
                    </small>

                  </div>

                </Col>


                <Col
                  xs={12}
                  md={6}
                  xl={3}
                >

                  <div
                    className="
                      border
                      rounded
                      p-3
                      h-100
                    "
                  >

                    <div
                      style={{
                        color:
                          "#2563eb",
                        fontWeight:
                          800,
                      }}
                    >
                      Pagos por acreditar
                    </div>

                    <h4 className="my-1">
                      {
                        kpis.pagosPendientes
                      }
                    </h4>

                    <small className="text-muted">
                      {
                        kpis.contratosPendientes
                      }{" "}
                      contratos sin firmar
                    </small>

                  </div>

                </Col>

              </Row>

            </Card.Body>

          </Card>


          {/* ===============================================
              GRÁFICOS
             =============================================== */}

          <Row className="g-3 mb-3">

            <Col
              xs={12}
              lg={7}
            >

              <Card className="dashboard-panel">

                <Card.Body>

                  <h6>
                    Evolución de ventas
                  </h6>

                  <div className="dashboard-chart">

                    <ResponsiveContainer
                      width="100%"
                      height="100%"
                    >

                      <LineChart
                        data={
                          evolucionVentas
                        }
                      >

                        <CartesianGrid
                          strokeDasharray="3 3"
                        />

                        <XAxis
                          dataKey="fecha"
                        />

                        <YAxis />

                        <Tooltip
                          formatter={(
                            value,
                            name
                          ) =>
                            name ===
                            "Ventas USD"
                              ? formatUSD(
                                  value
                                )
                              : value
                          }
                        />

                        <Legend />

                        <Line
                          type="monotone"
                          dataKey="ventas"
                          name="Ventas USD"
                          stroke="#2563eb"
                          strokeWidth={3}
                        />

                      </LineChart>

                    </ResponsiveContainer>

                  </div>

                </Card.Body>

              </Card>

            </Col>


            <Col
              xs={12}
              lg={5}
            >

              <Card className="dashboard-panel">

                <Card.Body>

                  <h6>
                    Ventas por sede
                  </h6>

                  <div className="dashboard-chart">

                    <ResponsiveContainer
                      width="100%"
                      height="100%"
                    >

                      <BarChart
                        data={
                          ventasPorSede
                        }
                      >

                        <CartesianGrid
                          strokeDasharray="3 3"
                        />

                        <XAxis
                          dataKey="sede"
                        />

                        <YAxis />

                        <Tooltip
                          formatter={(
                            value
                          ) =>
                            formatUSD(
                              value
                            )
                          }
                        />

                        <Legend />

                        <Bar
                          dataKey="total"
                          name="Ventas USD"
                          fill="#22c55e"
                        />

                      </BarChart>

                    </ResponsiveContainer>

                  </div>

                </Card.Body>

              </Card>

            </Col>

          </Row>


          {/* ===============================================
              TABLAS
             =============================================== */}

          <Row className="g-3 mb-3">

            <Col
              xs={12}
              lg={6}
            >

              <Card className="dashboard-panel">

                <Card.Body>

                  <h6>
                    Ranking de vendedores
                  </h6>

                  <div className="table-responsive">

                    <Table
                      size="sm"
                      hover
                    >

                      <thead>

                        <tr>
                          <th>
                            #
                          </th>

                          <th>
                            Vendedor
                          </th>

                          <th className="text-end">
                            Ventas
                          </th>

                          <th className="text-end">
                            Total
                          </th>

                          <th className="text-end">
                            Acreditado
                          </th>
                        </tr>

                      </thead>


                      <tbody>

                        {rankingVendedores.map(
                          (
                            vendedor,
                            index
                          ) => (

                            <tr
                              key={
                                vendedor.vendedor
                              }
                            >

                              <td>
                                {index +
                                  1}
                              </td>

                              <td>
                                {
                                  vendedor.vendedor
                                }
                              </td>

                              <td className="text-end">
                                {
                                  vendedor.ventas
                                }
                              </td>

                              <td className="text-end">
                                {formatUSD(
                                  vendedor.total
                                )}
                              </td>

                              <td className="text-end text-neto">
                                {formatUSD(
                                  vendedor.acreditado
                                )}
                              </td>

                            </tr>

                          )
                        )}

                      </tbody>

                    </Table>

                  </div>

                </Card.Body>

              </Card>

            </Col>


            <Col
              xs={12}
              lg={6}
            >

              <Card className="dashboard-panel">

                <Card.Body>

                  <h6>
                    Productos más vendidos
                  </h6>

                  <div className="table-responsive">

                    <Table
                      size="sm"
                      hover
                    >

                      <thead>

                        <tr>

                          <th>
                            #
                          </th>

                          <th>
                            Producto
                          </th>

                          <th className="text-end">
                            Cant.
                          </th>

                          <th className="text-end">
                            Total USD
                          </th>

                        </tr>

                      </thead>


                      <tbody>

                        {topProductos.map(
                          (
                            producto,
                            index
                          ) => (

                            <tr
                              key={
                                producto.producto
                              }
                            >

                              <td>
                                {index +
                                  1}
                              </td>

                              <td>
                                {
                                  producto.producto
                                }
                              </td>

                              <td className="text-end">
                                {
                                  producto.cantidad
                                }
                              </td>

                              <td className="text-end">
                                {formatUSD(
                                  producto.total
                                )}
                              </td>

                            </tr>

                          )
                        )}

                      </tbody>

                    </Table>

                  </div>

                </Card.Body>

              </Card>

            </Col>

          </Row>


          {/* ===============================================
              DIFERENCIAS FINANCIERAS
             =============================================== */}

          <Row className="g-3">

            <Col
              xs={12}
              lg={7}
            >

              <Card className="dashboard-panel">

                <Card.Body>

                  <h6>
                    Control de diferencias de fondeo
                  </h6>

                  <small className="text-muted">
                    Operaciones con diferencia superior a US$ 1.
                  </small>

                  <div className="table-responsive mt-2">

                    <Table
                      size="sm"
                      hover
                    >

                      <thead>

                        <tr>

                          <th>
                            Venta
                          </th>

                          <th>
                            Fecha
                          </th>

                          <th>
                            Financiera
                          </th>

                          <th className="text-end">
                            Fondeo esperado
                          </th>

                          <th className="text-end">
                            Fondeo real
                          </th>

                          <th className="text-end">
                            Diferencia neta
                          </th>

                          <th className="text-end">
                            Ajuste
                          </th>

                        </tr>

                      </thead>


                      <tbody>

                        {diferenciasFinancieras.length ===
                        0 ? (

                          <tr>

                            <td
                              colSpan={7}
                              className="
                                text-center
                                text-muted
                                py-3
                              "
                            >
                              Sin diferencias relevantes.
                            </td>

                          </tr>

                        ) : (

                          diferenciasFinancieras.map(
                            (
                              fila,
                              index
                            ) => (

                              <tr
                                key={`${fila.venta}-${index}`}
                              >

                                <td>
                                  {
                                    fila.venta
                                  }
                                </td>

                                <td>
                                  {formatDate(
                                    fila.fecha
                                  )}
                                </td>

                                <td>
                                  {
                                    fila.financiera
                                  }
                                </td>

                                <td className="text-end">
                                  {formatUSD(
                                    fila.esperado
                                  )}
                                </td>

                                <td className="text-end">
                                  {formatUSD(
                                    fila.real
                                  )}
                                </td>

                                <td
                                  className="text-end"
                                  style={{
                                    color:
                                      fila.diferencia <
                                      0
                                        ? "#dc2626"
                                        : "#16a34a",
                                    fontWeight:
                                      900,
                                  }}
                                >
                                  {formatUSD(
                                    fila.diferencia
                                  )}
                                </td>

                                <td className="text-end">
                                  {formatUSD(
                                    fila.ajuste
                                  )}
                                </td>

                              </tr>

                            )
                          )

                        )}

                      </tbody>

                    </Table>

                  </div>

                </Card.Body>

              </Card>

            </Col>


            {/* =============================================
                MAYORES SALDOS
               ============================================= */}

            <Col
              xs={12}
              lg={5}
            >

              <Card className="dashboard-panel">

                <Card.Body>

                  <h6>
                    Principales saldos pendientes
                  </h6>

                  <div className="table-responsive">

                    <Table
                      size="sm"
                      hover
                    >

                      <thead>

                        <tr>

                          <th>
                            Venta
                          </th>

                          <th>
                            Vendedor
                          </th>

                          <th>
                            Sede
                          </th>

                          <th className="text-end">
                            Saldo
                          </th>

                        </tr>

                      </thead>


                      <tbody>

                        {principalesSaldos.length ===
                        0 ? (

                          <tr>

                            <td
                              colSpan={4}
                              className="
                                text-center
                                text-muted
                              "
                            >
                              Sin saldos pendientes.
                            </td>

                          </tr>

                        ) : (

                          principalesSaldos.map(
                            (venta) => (

                              <tr
                                key={
                                  venta._id ||
                                  venta.nroventa
                                }
                              >

                                <td>
                                  {
                                    venta.nroventa
                                  }
                                </td>

                                <td>
                                  {
                                    venta._vendedor
                                  }
                                </td>

                                <td>
                                  {
                                    venta._sede
                                  }
                                </td>

                                <td className="text-end text-fee">
                                  {formatUSD(
                                    venta._saldo
                                  )}
                                </td>

                              </tr>

                            )
                          )

                        )}

                      </tbody>

                    </Table>

                  </div>

                </Card.Body>

              </Card>

            </Col>

          </Row>

        </>

      )}

    </div>

  );
}